// src/components/Footer.jsx
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { motion } from 'framer-motion'; // Assuming you want Framer Motion on the links too

// --- SVG Icon Components for Social Media ---

// Facebook Icon
const FacebookIcon = ({ className }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
    </svg>
);

// Twitter/X Icon
const TwitterIcon = ({ className }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22.54 6.78a10.42 10.42 0 0 1-3.04.83 5.3 5.3 0 0 0-3.6-1.46 5.3 5.3 0 0 0-5.29 5.29c0 .41.05.81.14 1.19A15 15 0 0 1 3 4.26a5.3 5.3 0 0 0 1.64 7.05 5.35 5.35 0 0 1-2.4-.66v.07a5.3 5.3 0 0 0 4.23 5.2 5.38 5.38 0 0 1-1.4.17 5.29 5.29 0 0 1-1.04-.1l.13 1.6A15.06 15.06 0 0 0 22.14 8a10.41 10.41 0 0 0 2.87-2.61L22.54 6.78z"/>
    </svg>
);

// Instagram Icon
const InstagramIcon = ({ className }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
        <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
    </svg>
);

const Footer = () => {
    
    // Define Link content
    const companyLinks = [
        { to: '/about-faq', label: 'About Us' },
        { to: '/how-it-works', label: 'How It Works' },
        { to: '/terms', label: 'Terms of Service' },
        { to: '/privacy', label: 'Privacy Policy' },
    ];

    const boroughLinks = [
        { to: '/boroughs/manhattan', label: 'Manhattan' },
        { to: '/boroughs/brooklyn', label: 'Brooklyn' },
        { to: '/boroughs/queens', label: 'Queens' },
        { to: '/boroughs/bronx', label: 'The Bronx' },
        { to: '/boroughs/staten-island', label: 'Staten Island' },
    ];
    
    const supportLinks = [
        { to: '/contact', label: 'Contact Support' },
        { to: '/faq', label: 'FAQs' },
        { to: '/guidelines', label: 'Community Guidelines' },
        { to: '/safety', label: 'Safety Hub' },
    ];

    // Simple fade-in animation for the whole footer
    const footerVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } }
    };


    return (
        <motion.footer 
            className="footer-root"
            variants={footerVariants}
            initial="hidden"
            whileInView="visible" // Animates when scrolled into view
            viewport={{ once: true, amount: 0.3 }}
        >
            <div className="footer-container">
                <div className="footer-grid">
                    
                    {/* 1. Branding and Social Column */}
                    <div className="footer-column">
                        <h4 className="footer-brand-title">NYC Connect</h4>
                        <p className="footer-brand-text">
                            The online community dedicated to New York City residents. Find your local connections.
                        </p>
                        <div className="footer-social-icons">
                            <motion.a 
                                href="https://facebook.com" 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="footer-social-link"
                                whileHover={{ scale: 1.1, color: '#3b5998' }} // Facebook blue on hover
                                whileTap={{ scale: 0.9 }}
                            >
                                <FacebookIcon className="social-icon" />
                            </motion.a>
                            <motion.a 
                                href="https://twitter.com" 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="footer-social-link"
                                whileHover={{ scale: 1.1, color: '#1da1f2' }} // Twitter/X blue on hover
                                whileTap={{ scale: 0.9 }}
                            >
                                <TwitterIcon className="social-icon" />
                            </motion.a>
                            <motion.a 
                                href="https://instagram.com" 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="footer-social-link"
                                whileHover={{ scale: 1.1, color: '#E1306C' }} // Instagram gradient color on hover
                                whileTap={{ scale: 0.9 }}
                            >
                                <InstagramIcon className="social-icon" />
                            </motion.a>
                        </div>
                    </div>

                    {/* 2. Company Links Column */}
                    <div className="footer-column">
                        <h4 className="footer-column-title">Company</h4>
                        <ul className="footer-link-list">
                            {companyLinks.map((link) => (
                                <li key={link.label}>
                                    <RouterLink to={link.to} className="footer-link">
                                        {link.label}
                                    </RouterLink>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* 3. Borough Links Column */}
                    <div className="footer-column">
                        <h4 className="footer-column-title">Boroughs</h4>
                        <ul className="footer-link-list">
                           {boroughLinks.map((link) => (
                                <li key={link.label}>
                                    <RouterLink to={link.to} className="footer-link">
                                        {link.label}
                                    </RouterLink>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* 4. Support Links Column */}
                    <div className="footer-column">
                        <h4 className="footer-column-title">Support</h4>
                        <ul className="footer-link-list">
                           {supportLinks.map((link) => (
                                <li key={link.label}>
                                    <RouterLink to={link.to} className="footer-link">
                                        {link.label}
                                    </RouterLink>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* Copyright Section */}
                <div className="footer-copyright">
                    <small>
                        &copy; {new Date().getFullYear()} NYC Connect. All rights reserved.
                    </small>
                </div>
            </div>
        </motion.footer>
    );
};

export default Footer;